import { useState } from 'react'
import { cn } from '../../../shared'
import type { ParsedPayslip, DetectedDienstwagen } from '../services/payslipParser'
import DienstwagenCard from './DienstwagenCard'
import { BUNDESLAENDER } from '../../../lib/german-tax-calculator'

interface PayslipConfirmModalProps {
  data: ParsedPayslip
  onConfirm: (data: ParsedPayslip) => void
  onCancel: () => void
}

function NichtErkanntBadge() {
  return (
    <span className="text-xs text-amber-600 dark:text-amber-400">⚠️ Nicht erkannt</span>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="col-span-2 text-sm font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mt-2 first:mt-0">
      {children}
    </h3>
  )
}

export default function PayslipConfirmModal({ data, onConfirm, onCancel }: PayslipConfirmModalProps) {
  const [local, setLocal] = useState<ParsedPayslip>({ ...data, dienstwagen: [...data.dienstwagen] })

  const inputClass = "border border-zinc-200 rounded px-3 py-2 text-zinc-900 dark:text-zinc-100 dark:bg-zinc-800 dark:border-zinc-700 w-full"
  const nullClass = "opacity-60"

  const update = <K extends keyof ParsedPayslip>(key: K, value: ParsedPayslip[K]) => {
    setLocal(prev => ({ ...prev, [key]: value }))
  }

  const updateDienstwagen = (index: number, field: keyof DetectedDienstwagen, value: DetectedDienstwagen[keyof DetectedDienstwagen]) => {
    setLocal(prev => ({
      ...prev,
      dienstwagen: prev.dienstwagen.map((dw, i) =>
        i === index ? { ...dw, [field]: value } : dw
      ),
    }))
  }

  const removeDienstwagen = (index: number) => {
    setLocal(prev => ({
      ...prev,
      dienstwagen: prev.dienstwagen.filter((_, i) => i !== index),
    }))
  }

  const showDienstwagen = local.dienstwagen.length > 0
  const showFahrrad = local.fahrradLeasingVorhanden === true || local.fahrradListenpreis != null || local.fahrradGwvProzent != null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        className="max-w-lg w-full bg-white dark:bg-zinc-900 rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Ausgelesene Werte bestätigen
        </h2>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1 mb-5">
          Bitte überprüfe die ausgelesenen Werte und korrigiere sie falls nötig.
        </p>

        {/* Fields */}
        <div className="grid grid-cols-2 gap-4">
          {/* ── Gehalt & Steuern ── */}
          <SectionHeader>Gehalt &amp; Steuern</SectionHeader>

          {/* Bruttogehalt */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Bruttogehalt
              {data.bruttogehalt === null && <NichtErkanntBadge />}
            </label>
            <div className="relative">
              <input
                type="number"
                placeholder="Nicht erkannt"
                value={local.bruttogehalt ?? ''}
                onChange={e => update('bruttogehalt', e.target.value === '' ? null : Number(e.target.value))}
                className={cn(inputClass, data.bruttogehalt === null && nullClass)}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
            </div>
          </div>

          {/* Steuerklasse */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Steuerklasse
              {data.steuerklasse === null && <NichtErkanntBadge />}
            </label>
            <input
              type="number"
              min={1}
              max={6}
              placeholder="Nicht erkannt"
              value={local.steuerklasse ?? ''}
              onChange={e => update('steuerklasse', e.target.value === '' ? null : Number(e.target.value))}
              className={cn(inputClass, data.steuerklasse === null && nullClass)}
            />
          </div>

          {/* Kirchensteuer */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Kirchensteuer
              {data.kirchensteuer === null && <NichtErkanntBadge />}
            </label>
            <label className="flex items-center gap-2 mt-1">
              <input
                type="checkbox"
                checked={local.kirchensteuer ?? false}
                onChange={e => update('kirchensteuer', e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 dark:border-zinc-600"
              />
              <span className="text-sm text-zinc-600 dark:text-zinc-400">
                {local.kirchensteuer ? 'Ja' : 'Nein'}
              </span>
            </label>
          </div>

          {/* Bundesland */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Bundesland (Arbeitgeber)
              {data.bundesland === null && <NichtErkanntBadge />}
            </label>
            <select
              value={local.bundesland ?? ''}
              onChange={e => update('bundesland', e.target.value === '' ? null : e.target.value)}
              className={cn(inputClass, data.bundesland === null && nullClass)}
            >
              <option value="">Nicht erkannt</option>
              {BUNDESLAENDER.map(bl => (
                <option key={bl} value={bl}>{bl}</option>
              ))}
            </select>
          </div>

          {/* Kinderfreibetrag */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Kinderfreibetrag
              {data.kinderfreibetrag === null && <NichtErkanntBadge />}
            </label>
            <input
              type="number"
              placeholder="Nicht erkannt"
              value={local.kinderfreibetrag ?? ''}
              onChange={e => update('kinderfreibetrag', e.target.value === '' ? null : Number(e.target.value))}
              className={cn(inputClass, data.kinderfreibetrag === null && nullClass)}
            />
          </div>

          {/* Anzahl Kinder */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Anzahl Kinder
              {data.anzahlKinder === null && <NichtErkanntBadge />}
            </label>
            <input
              type="number"
              min={0}
              placeholder="Nicht erkannt"
              value={local.anzahlKinder ?? ''}
              onChange={e => update('anzahlKinder', e.target.value === '' ? null : Number(e.target.value))}
              className={cn(inputClass, data.anzahlKinder === null && nullClass)}
            />
          </div>

          {/* ── Versicherung ── */}
          <SectionHeader>Versicherung</SectionHeader>

          {/* Krankenversicherung */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Krankenversicherung
              {data.krankenversicherungTyp === null && <NichtErkanntBadge />}
            </label>
            <select
              value={local.krankenversicherungTyp ?? ''}
              onChange={e => update('krankenversicherungTyp', e.target.value === '' ? null : e.target.value as 'GKV' | 'PKV')}
              className={cn(inputClass, data.krankenversicherungTyp === null && nullClass)}
            >
              <option value="">Nicht erkannt</option>
              <option value="GKV">GKV</option>
              <option value="PKV">PKV</option>
            </select>
          </div>

          {/* KV-Zusatzbeitrag */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              KV-Kassenzusatzbeitrag
              {data.kvZusatzbeitrag === null && <NichtErkanntBadge />}
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                placeholder="Nicht erkannt"
                value={local.kvZusatzbeitrag ?? ''}
                onChange={e => update('kvZusatzbeitrag', e.target.value === '' ? null : Number(e.target.value))}
                className={cn(inputClass, data.kvZusatzbeitrag === null && nullClass)}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">%</span>
            </div>
            {local.kvZusatzbeitrag != null && local.krankenversicherungTyp !== 'PKV' && (
              <p className="text-xs text-zinc-400 mt-1">
                Gesamt-KV-Rate: 7,3% + {local.kvZusatzbeitrag.toFixed(1)}% = {(7.3 + local.kvZusatzbeitrag).toFixed(1)}%
              </p>
            )}
            {local.krankenversicherungTyp !== 'PKV' && (
              <p className="text-xs text-zinc-400 mt-0.5">Nur der kassenindividuelle Zusatz (nicht die Basis 7,3%)</p>
            )}
          </div>

          {/* PKV fields — only when PKV selected */}
          {local.krankenversicherungTyp === 'PKV' && (<>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                PKV-Beitrag AN (KV)
                {data.pkvMonatsbeitragKV === null && <NichtErkanntBadge />}
              </label>
              <div className="relative">
                <input type="number" placeholder="0" value={local.pkvMonatsbeitragKV ?? ''}
                  onChange={e => update('pkvMonatsbeitragKV', e.target.value === '' ? null : Number(e.target.value))}
                  className={cn(inputClass, data.pkvMonatsbeitragKV === null && nullClass)} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                PKV-Beitrag AN (PV)
                {data.pkvMonatsbeitragPV === null && <NichtErkanntBadge />}
              </label>
              <div className="relative">
                <input type="number" placeholder="0" value={local.pkvMonatsbeitragPV ?? ''}
                  onChange={e => update('pkvMonatsbeitragPV', e.target.value === '' ? null : Number(e.target.value))}
                  className={cn(inputClass, data.pkvMonatsbeitragPV === null && nullClass)} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                AG-Zuschuss KV
                {data.pkvAGZuschussKV === null && <NichtErkanntBadge />}
              </label>
              <div className="relative">
                <input type="number" placeholder="0" value={local.pkvAGZuschussKV ?? ''}
                  onChange={e => update('pkvAGZuschussKV', e.target.value === '' ? null : Number(e.target.value))}
                  className={cn(inputClass, data.pkvAGZuschussKV === null && nullClass)} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                AG-Zuschuss PV
                {data.pkvAGZuschussPV === null && <NichtErkanntBadge />}
              </label>
              <div className="relative">
                <input type="number" placeholder="0" value={local.pkvAGZuschussPV ?? ''}
                  onChange={e => update('pkvAGZuschussPV', e.target.value === '' ? null : Number(e.target.value))}
                  className={cn(inputClass, data.pkvAGZuschussPV === null && nullClass)} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
              </div>
            </div>
          </>)}

          {/* ── Altersvorsorge ── */}
          <SectionHeader>Altersvorsorge</SectionHeader>

          {/* bAV-Beitrag AN */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              bAV-Beitrag (AN)
              {data.bavBeitragAN === null && <NichtErkanntBadge />}
            </label>
            <div className="relative">
              <input
                type="number"
                placeholder="0"
                value={local.bavBeitragAN ?? ''}
                onChange={e => update('bavBeitragAN', e.target.value === '' ? null : Number(e.target.value))}
                className={cn(inputClass, data.bavBeitragAN === null && nullClass)}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
            </div>
          </div>

          {/* bAV-Beitrag AG */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              bAV-Beitrag (AG)
              {data.bavBeitragAG === null && <NichtErkanntBadge />}
            </label>
            <div className="relative">
              <input
                type="number"
                placeholder="0"
                value={local.bavBeitragAG ?? ''}
                onChange={e => update('bavBeitragAG', e.target.value === '' ? null : Number(e.target.value))}
                className={cn(inputClass, data.bavBeitragAG === null && nullClass)}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
            </div>
          </div>

          {/* Pensionskasse-Beitrag */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Pensionskasse
              {data.pkBeitrag === null && <NichtErkanntBadge />}
            </label>
            <div className="relative">
              <input
                type="number"
                placeholder="0"
                value={local.pkBeitrag ?? ''}
                onChange={e => update('pkBeitrag', e.target.value === '' ? null : Number(e.target.value))}
                className={cn(inputClass, data.pkBeitrag === null && nullClass)}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
            </div>
          </div>

          {/* Spacer */}
          <div />

          {/* ── Dienstwagen (Array) ── */}
          {showDienstwagen && (
            <>
              <SectionHeader>Dienstwagen ({local.dienstwagen.length})</SectionHeader>

              {local.dienstwagen.map((dw, index) => (
                <DienstwagenCard
                  key={index}
                  dw={dw}
                  index={index}
                  inputClass={inputClass}
                  onUpdate={(field, value) => updateDienstwagen(index, field, value)}
                  onRemove={() => removeDienstwagen(index)}
                />
              ))}
            </>
          )}

          {/* ── Firmenfitness ── */}
          {(local.firmenfitnessBeitrag ?? 0) > 0 && (
            <>
              <SectionHeader>Firmenfitness</SectionHeader>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Eigenanteil / Monat
                  {data.firmenfitnessBeitrag === null && <NichtErkanntBadge />}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    placeholder="0"
                    value={local.firmenfitnessBeitrag ?? ''}
                    onChange={e => update('firmenfitnessBeitrag', e.target.value === '' ? null : Number(e.target.value))}
                    className={cn(inputClass, data.firmenfitnessBeitrag === null && nullClass)}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
                </div>
              </div>
              <div />
            </>
          )}

          {/* ── Fahrrad-Leasing ── */}
          {showFahrrad && (
            <>
              <SectionHeader>Fahrrad-Leasing</SectionHeader>

              {/* Listenpreis */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Listenpreis (UVP)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    placeholder="0"
                    value={local.fahrradListenpreis ?? ''}
                    onChange={e => update('fahrradListenpreis', e.target.value === '' ? null : Number(e.target.value))}
                    className={inputClass}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
                </div>
              </div>

              {/* GwV-Prozentsatz */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  GwV-Prozentsatz
                </label>
                <select
                  value={local.fahrradGwvProzent ?? 0.25}
                  onChange={e => update('fahrradGwvProzent', Number(e.target.value))}
                  className={inputClass}
                >
                  <option value={0.25}>0,25%</option>
                  <option value={0.5}>0,5%</option>
                  <option value={1.0}>1,0%</option>
                </select>
              </div>

              {/* Gehaltsumwandlung / Leasingrate */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Gehaltsumwandlung / Rate
                  {data.fahrradGehaltsumwandlung === null && <NichtErkanntBadge />}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    placeholder="0"
                    value={local.fahrradGehaltsumwandlung ?? ''}
                    onChange={e => update('fahrradGehaltsumwandlung', e.target.value === '' ? null : Number(e.target.value))}
                    className={cn(inputClass, data.fahrradGehaltsumwandlung === null && nullClass)}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">€</span>
                </div>
              </div>

              <div />
            </>
          )}
        </div>

        {/* Debug Panel */}
        <details className="mt-4">
          <summary className="text-xs text-zinc-400 cursor-pointer hover:text-zinc-600 select-none">
            🔍 Rohe KI-Antwort (Debug)
          </summary>
          <pre className="mt-2 text-xs bg-zinc-100 dark:bg-zinc-800 rounded p-3 overflow-auto max-h-64 text-zinc-600 dark:text-zinc-300">
            {JSON.stringify(data, null, 2)}
          </pre>
        </details>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={() => onConfirm(local)}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
          >
            Übernehmen
          </button>
        </div>
      </div>
    </div>
  )
}
